import { createContext, useContext, useReducer, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import loginReducer, { initialState } from "./reducer";
import { loginRequest, loginSuccess, loginFailure, logout } from "./actions";
import { loginUser } from "./api";
import { ensureAuthCookiesFromResponse, eraseAuthCookies, getCookie } from "../../../lib/utils/cookieUtil";
import { removeLocalStorage, LOCAL_STORAGE_KEYS } from "../../../lib/utils/localStorageUtil";
import { initFeatureAccess, clearFeatureAccess, clearLegacyUserDetailsStorage } from "../../../lib/utils/featureAccess";

const AuthContext = createContext(null);

function getHydratedState() {
  return {
    ...initialState,
    user: null,
    // HttpOnly server cookies are not readable; stay active until 401
    sessionExpired: false,
    system_id: getCookie("system_id"),
  };
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(loginReducer, undefined, getHydratedState);

  useEffect(() => {
    clearLegacyUserDetailsStorage();
  }, []);

  const doLogin = useCallback(async credentials => {
    dispatch(loginRequest());
    try {
      const response = await loginUser(credentials);
      ensureAuthCookiesFromResponse(response);
      initFeatureAccess(response);
      dispatch(
        loginSuccess({
          system_id: response.system_id ?? null,
          sessionExpired: false,
        })
      );
      return response;
    } catch (error) {
      dispatch(loginFailure(error.response?.data || error.message));
      throw error;
    }
  }, []);

  const doLogout = useCallback(() => {
    eraseAuthCookies();
    clearFeatureAccess();
    clearLegacyUserDetailsStorage();
    removeLocalStorage(LOCAL_STORAGE_KEYS.FILTERBAR_STATE);
    removeLocalStorage(LOCAL_STORAGE_KEYS.GLOBAL_FILTERS);
    removeLocalStorage(LOCAL_STORAGE_KEYS.CUSTOM_FEATURE_OPTIONS);
    sessionStorage.clear();
    dispatch(logout());
    window.location.href = "/login";
  }, []);

  const value = {
    ...state,
    login: doLogin,
    logout: doLogout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}

export default useAuth;
